import { Component, HostListener, OnInit, inject, signal } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { filter, finalize } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  StaffUserApiService,
  StaffUserListMeta,
  StaffUserListQuery,
  StaffUserRow,
} from 'src/app/core/services/staff-user-api.service';
import {
  PageSurfaceHeaderComponent,
  PageSurfaceTab,
} from 'src/app/shared/page-surface-header/page-surface-header.component';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListModuleFilters,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
} from 'src/app/shared/components/list-module';
import { goToAdminUserDetail, navigateAdminUserEdit } from './admin-user-route.util';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import { PermissionService } from 'src/app/core/services/permission.service';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { RestoreConfirmModalComponent } from 'src/app/shared/components/restore-confirm-modal/restore-confirm-modal.component';
import {
  DeleteConfirmModalComponent,
  type DeleteConfirmVariant,
} from 'src/app/shared/components/delete-confirm-modal/delete-confirm-modal.component';
import { ManageAdminUsersTableComponent } from './manage-admin-users-table/manage-admin-users-table.component';
import { APP_DATETIME_FORMAT } from 'src/app/shared/pipes/app-date.pipe';
import { formatPhoneInternationalDisplay } from 'src/app/shared/utils/international-phone';
import { downloadExcelFromJson } from 'src/app/shared/utils/excel.util';
import { trimmedOrUndefined } from 'src/app/shared/utils/string.util';
import {
  ConfirmActionDialogComponent,
  type ConfirmActionDialogData,
} from 'src/app/pages/manage-client/confirm-action-dialog.component';

export type StaffUserListView = 'active' | 'deleted';

@Component({
  selector: 'app-manage-admin-users',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    MaterialModule,
    MatDialogModule,
    IconModule,
    FormsModule,
    RouterModule,
    PageSurfaceHeaderComponent,
    ListModuleSurfaceComponent,
    ManageAdminUsersTableComponent,
    HasPermissionPipe,
    RestoreConfirmModalComponent,
    DeleteConfirmModalComponent,
  ],
  templateUrl: './manage-admin-users.component.html',
})
export class ManageAdminUsersComponent implements OnInit {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  private readonly userApi = inject(StaffUserApiService);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly permissionService = inject(PermissionService);

  view: StaffUserListView = 'active';
  userSurfaceTabs: PageSurfaceTab[] = [];

  tableData: StaffUserRow[] = [];
  isLoading = false;
  exporting = false;
  errorMessage = '';

  /** Stable config refs — avoid getters that recreate config each CD (breaks search debounce). */
  readonly listSurfaceConfigActive: ListModuleSurfaceConfig = {
    toolbar: {
      search: {
        placeholder: 'Search name, email, phone…',
        deletedPlaceholder: 'Search deleted users…',
      },
      status: {
        options: [
          { value: '', label: 'All status' },
          { value: 'Active', label: 'Active' },
          { value: 'InActive', label: 'Inactive' },
        ],
      },
      dateRange: {
        placeholder: 'Created date range',
        deletedPlaceholder: 'Deleted date range',
      },
    },
    pagination: true,
  };

  readonly listSurfaceConfigDeleted: ListModuleSurfaceConfig = {
    toolbar: {
      search: {
        placeholder: 'Search name, email, phone…',
        deletedPlaceholder: 'Search deleted users…',
      },
      status: false,
      dateRange: {
        placeholder: 'Created date range',
        deletedPlaceholder: 'Deleted date range',
      },
    },
    pagination: true,
  };

  listFilters: ListModuleFilters = { ...DEFAULT_LIST_MODULE_FILTERS };

  page = 1;
  readonly pageSize = 10;
  meta: StaffUserListMeta | null = null;

  showDeleteModal = false;
  deleteLoading = signal(false);
  deleteVariant: DeleteConfirmVariant = 'soft-delete';
  deleteError = '';
  selectedUser: StaffUserRow | null = null;

  showRestoreModal = false;
  restoreLoading = signal(false);

  /** User id while a resend-link request is in flight (avoids blocking the whole list). */
  userSideEffectBusyId: number | null = null;

  openActionMenuKey: string | null = null;

  ngOnInit(): void {
    this.userSurfaceTabs = this.buildUserSurfaceTabs();
    this.view = this.resolveInitialView();
    this.loadUsers();
  }

  private buildUserSurfaceTabs(): PageSurfaceTab[] {
    const tabs: PageSurfaceTab[] = [];
    if (this.permissionService.isAccessAllowed(this.P.USER_LIST)) {
      tabs.push({ key: 'active', label: 'Active' });
    }
    if (this.permissionService.isAccessAllowed(this.P.USER_ARCHIVED_LIST)) {
      tabs.push({ key: 'deleted', label: 'Deleted' });
    }
    return tabs.length ? tabs : [{ key: 'active', label: 'Active' }];
  }

  private resolveInitialView(): StaffUserListView {
    const keys = new Set(this.userSurfaceTabs.map((t) => t.key));
    if (keys.has(this.view)) {
      return this.view;
    }
    return (this.userSurfaceTabs[0]?.key as StaffUserListView) ?? 'active';
  }

  get headingSubtitle(): string {
    return this.view === 'deleted'
      ? 'Soft-deleted users — restore to return them to the active list'
      : 'View and manage super-admin portal users';
  }

  get emptyMessage(): string {
    return this.view === 'deleted'
      ? 'No deleted users match your filters.'
      : 'No users match your filters.';
  }

  onUserTabKey(key: string): void {
    if (key === 'active' || key === 'deleted') {
      this.setView(key);
    }
  }

  setView(mode: StaffUserListView): void {
    if (this.view === mode) return;
    if (mode === 'deleted' && !this.permissionService.isAccessAllowed(this.P.USER_ARCHIVED_LIST)) {
      this.snackBar.open('You do not have permission to view deleted users.', 'Close', {
        duration: 5000,
      });
      return;
    }
    if (mode === 'active' && !this.permissionService.isAccessAllowed(this.P.USER_LIST)) {
      this.snackBar.open('You do not have permission to view active users.', 'Close', {
        duration: 5000,
      });
      return;
    }
    this.closeActionMenu();
    this.view = mode;
    this.page = 1;
    this.listFilters = { ...DEFAULT_LIST_MODULE_FILTERS };
    this.closeDeleteModal();
    this.closeRestoreModal();
    this.loadUsers();
  }

  loadUsers(): void {
    this.isLoading = true;
    this.errorMessage = '';
    const query: Record<string, string | number> = {
      page: this.page,
      page_size: this.pageSize,
    };
    if (this.listFilters.searchQuery.trim()) {
      query['searchText'] = this.listFilters.searchQuery.trim();
    }

    if (this.view === 'active') {
      if (this.listFilters.dateRangeStart) query['created_at_from'] = this.listFilters.dateRangeStart;
      if (this.listFilters.dateRangeEnd) query['created_at_to'] = this.listFilters.dateRangeEnd;
      if (this.listFilters.status) query['status'] = this.listFilters.status;
    } else {
      if (this.listFilters.dateRangeStart) query['deleted_at_from'] = this.listFilters.dateRangeStart;
      if (this.listFilters.dateRangeEnd) query['deleted_at_to'] = this.listFilters.dateRangeEnd;
    }

    const req =
      this.view === 'deleted'
        ? this.userApi.listDeletedUsers(query)
        : this.userApi.listUsers(query);

    req.pipe(finalize(() => (this.isLoading = false))).subscribe({
      next: (res) => {
        if (res?.status && res.data) {
          this.tableData = res.data.result ?? [];
          this.meta = res.data.meta ?? null;
        } else {
          this.tableData = [];
          this.meta = null;
          this.errorMessage = res?.message || 'Could not load users.';
        }
      },
      error: (err) => {
        this.tableData = [];
        this.meta = null;
        this.errorMessage =
          err?.error?.message || err?.message || 'Could not load users.';
        this.snackBar.open(this.errorMessage, 'Dismiss', { duration: 5000 });
      },
    });
  }

  onListFiltersChange(patch: Partial<ListModuleFilters>): void {
    this.listFilters = { ...this.listFilters, ...patch };
    this.page = 1;
    this.loadUsers();
  }

  onListPageChange(next: number): void {
    if (!this.meta || next < 1 || next > this.meta.lastPage) return;
    this.page = next;
    this.loadUsers();
  }

  actionMenuRowKey(index: number): string {
    return this.view === 'deleted' ? `deleted-${index}` : `active-${index}`;
  }

  toggleActionMenu(key: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openActionMenuKey = this.openActionMenuKey === key ? null : key;
  }

  closeActionMenu(): void {
    this.openActionMenuKey = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!(event.target as HTMLElement).closest('.agent-action-menu')) {
      this.openActionMenuKey = null;
    }
  }

  goToUserDetail(row: StaffUserRow): void {
    if (this.view !== 'active') return;
    goToAdminUserDetail(this.router, this.userApi, row, (message) =>
      this.snackBar.open(message, 'Dismiss', { duration: 5000 }),
    );
  }

  menuEdit(row: StaffUserRow): void {
    this.closeActionMenu();
    navigateAdminUserEdit(this.router, this.userApi, row, (message) =>
      this.snackBar.open(message, 'Dismiss', { duration: 5000 }),
    );
  }

  menuDelete(row: StaffUserRow): void {
    this.closeActionMenu();
    this.confirmDelete(row);
  }

  menuRestore(row: StaffUserRow): void {
    this.closeActionMenu();
    this.confirmRestore(row);
  }

  menuPermanentDelete(row: StaffUserRow): void {
    this.closeActionMenu();
    this.deleteVariant = 'permanent';
    this.deleteError = '';
    this.selectedUser = row;
    this.showDeleteModal = true;
  }

  menuToggleStatus(row: StaffUserRow): void {
    this.closeActionMenu();
    this.toggleStatus(row);
  }

  menuResendLink(row: StaffUserRow): void {
    this.closeActionMenu();
    if (this.view !== 'active') return;
    this.openResendPasswordLink(row);
  }

  /** Shows confirmation dialog then sends the invitation/reset email. */
  openResendPasswordLink(row: StaffUserRow): void {
    const name = trimmedOrUndefined(row.full_name) ?? row.email_id;
    const dialogData: ConfirmActionDialogData = {
      title: 'Resend link?',
      message: `Send the welcome / password setup email again for ${name}?`,
      confirmLabel: 'Resend link',
      confirmColor: 'primary',
    };
    const ref = this.dialog.open(ConfirmActionDialogComponent, {
      width: '440px',
      data: dialogData,
      disableClose: true,
    });
    ref
      .afterClosed()
      .pipe(filter((ok) => ok === true))
      .subscribe(() => this.executeResendPasswordLink(row));
  }

  private executeResendPasswordLink(row: StaffUserRow): void {
    this.userSideEffectBusyId = row.id;
    this.userApi
      .resendUserPasswordLink(row.id)
      .pipe(finalize(() => (this.userSideEffectBusyId = null)))
      .subscribe({
        next: (res) => {
          const msg =
            (res as { message?: string })?.message?.trim() ?? 'Email sent successfully.';
          this.showSuccessToast(msg);
        },
        error: (err: unknown) => {
          const msg =
            (err as { error?: { message?: string }; message?: string })?.error?.message ??
            (err as { message?: string })?.message ??
            'Could not resend email.';
          this.showErrorToast(msg);
        },
      });
  }

  private showSuccessToast(message: string): void {
    this.snackBar.open(message, 'Dismiss', {
      duration: 5000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
    });
  }

  private showErrorToast(message: string): void {
    this.snackBar.open(message, 'Dismiss', {
      duration: 8000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['app-snackbar', 'app-snackbar-error'],
    });
  }

  confirmDelete(user: StaffUserRow): void {
    this.deleteVariant = 'soft-delete';
    this.deleteError = '';
    this.selectedUser = user;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.selectedUser = null;
    this.deleteError = '';
    this.deleteVariant = 'soft-delete';
  }

  get deleteModalHeading(): string {
    return this.deleteVariant === 'permanent'
      ? 'Delete user permanently?'
      : 'Remove user?';
  }

  get deleteModalMessage(): string {
    if (this.deleteVariant !== 'permanent') {
      return '';
    }
    const name = trimmedOrUndefined(this.selectedUser?.full_name) ?? 'this user';
    return `This will permanently remove ${name} from the platform. This cannot be undone.`;
  }

  get deleteModalConfirmLabel(): string {
    return this.deleteVariant === 'permanent' ? 'Delete permanently' : 'Remove';
  }

  get deleteModalLoadingLabel(): string {
    return this.deleteVariant === 'permanent' ? 'Deleting…' : 'Removing…';
  }

  submitDelete(): void {
    if (!this.selectedUser) return;
    if (this.deleteVariant === 'permanent') {
      this.runPermanentDelete();
      return;
    }
    this.deleteError = '';
    this.deleteLoading.set(true);
    this.userApi
      .deleteUser(this.selectedUser.id)
      .pipe(finalize(() => this.deleteLoading.set(false)))
      .subscribe({
        next: (res) => {
          this.snackBar.open(res?.message || 'User removed successfully.', 'Dismiss', {
            duration: 5000,
          });
          this.closeDeleteModal();
          this.loadUsers();
        },
        error: (err) => {
          const msg =
            err?.error?.message || err?.message || 'Could not remove user.';
          this.deleteError = msg;
          this.snackBar.open(msg, 'Dismiss', { duration: 6000 });
        },
      });
  }

  private runPermanentDelete(): void {
    if (!this.selectedUser) return;
    this.deleteError = '';
    this.deleteLoading.set(true);
    this.userApi
      .permanentDeleteUser(this.selectedUser.id)
      .pipe(finalize(() => this.deleteLoading.set(false)))
      .subscribe({
        next: (res) => {
          this.snackBar.open(
            res?.message || 'User permanently deleted.',
            'Dismiss',
            { duration: 5000 },
          );
          this.closeDeleteModal();
          if (this.tableData.length <= 1 && this.page > 1) {
            this.page -= 1;
          }
          this.loadUsers();
        },
        error: (err) => {
          const msg =
            err?.error?.message || err?.message || 'Could not permanently delete user.';
          this.deleteError = msg;
          this.snackBar.open(msg, 'Dismiss', { duration: 6000 });
        },
      });
  }

  confirmRestore(user: StaffUserRow): void {
    this.selectedUser = user;
    this.showRestoreModal = true;
  }

  closeRestoreModal(): void {
    this.showRestoreModal = false;
    this.selectedUser = null;
  }

  submitRestore(): void {
    if (!this.selectedUser) return;
    this.restoreLoading.set(true);
    this.userApi
      .restoreUser(this.selectedUser.id)
      .pipe(finalize(() => this.restoreLoading.set(false)))
      .subscribe({
        next: (res) => {
          this.snackBar.open(res?.message || 'User restored successfully.', 'Dismiss', {
            duration: 5000,
          });
          this.closeRestoreModal();
          this.loadUsers();
        },
        error: (err) => {
          this.snackBar.open(
            err?.error?.message || err?.message || 'Could not restore user.',
            'Dismiss',
            { duration: 6000 },
          );
          this.closeRestoreModal();
        },
      });
  }

  /** Downloads `.xlsx` of all rows matching current filters and active/deleted tab. */
  exportExcel(): void {
    if (this.exporting) {
      return;
    }
    this.exporting = true;
    this.userApi
      .fetchAllStaffUserRows(this.exportListQuery(500), this.view === 'deleted')
      .pipe(finalize(() => (this.exporting = false)))
      .subscribe({
        next: (rows) => {
          if (!rows.length) {
            this.snackBar.open('No rows match the current filters.', 'Dismiss', {
              duration: 4000,
              horizontalPosition: 'end',
              verticalPosition: 'top',
            });
            return;
          }
          const data = this.buildExcelExportRows(rows);
          const sheetLabel = this.view === 'deleted' ? 'Deleted users' : 'Users';
          const stamp = new Date().toISOString().slice(0, 10);
          const segment = this.view === 'deleted' ? 'deleted' : 'active';
          void downloadExcelFromJson(
            data,
            sheetLabel,
            `users-export-${segment}-${stamp}.xlsx`,
          ).then(() => {
            this.snackBar.open(
              `Exported ${rows.length} row${rows.length === 1 ? '' : 's'}.`,
              'Dismiss',
              { duration: 5000, horizontalPosition: 'end', verticalPosition: 'top' },
            );
          });
        },
        error: (err) => {
          const msg =
            (err as { error?: { message?: string }; message?: string })?.error?.message ??
            (err as { message?: string })?.message ??
            'Could not export users.';
          this.snackBar.open(msg, 'Dismiss', {
            duration: 6000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
          });
        },
      });
  }

  private exportListQuery(chunkSize: number): Omit<StaffUserListQuery, 'page'> {
    const query: Omit<StaffUserListQuery, 'page'> = {
      page_size: chunkSize,
    };
    if (this.listFilters.searchQuery.trim()) {
      query.searchText = this.listFilters.searchQuery.trim();
    }
    if (this.view === 'active') {
      if (this.listFilters.dateRangeStart) {
        query.created_at_from = this.listFilters.dateRangeStart;
      }
      if (this.listFilters.dateRangeEnd) {
        query.created_at_to = this.listFilters.dateRangeEnd;
      }
      if (this.listFilters.status) {
        query.status = this.listFilters.status as 'Active' | 'InActive';
      }
    } else {
      if (this.listFilters.dateRangeStart) {
        query.deleted_at_from = this.listFilters.dateRangeStart;
      }
      if (this.listFilters.dateRangeEnd) {
        query.deleted_at_to = this.listFilters.dateRangeEnd;
      }
    }
    return query;
  }

  private buildExcelExportRows(rows: StaffUserRow[]): Record<string, string | number>[] {
    const deleted = this.view === 'deleted';
    return rows.map((row) => {
      const display = formatPhoneInternationalDisplay(
        row.mobile_number,
        row.country_code ?? null,
        row.country_code ?? null,
      );
      const phone = display === '—' ? '' : display;
      const role = row.user_roles?.[0]?.role;
      const record: Record<string, string | number> = {
        'User ID': row.unique_id ?? (row.id == null ? '' : `ID-${row.id}`),
        Name: row.full_name ?? '',
        Role: role?.role_name ?? role?.slug ?? '',
        Phone: phone,
        Email: row.email_id ?? '',
        Status: row.status === 'InActive' ? 'Inactive' : (row.status ?? ''),
        'Created (UTC)': this.formatExportCellDate(row.created_at),
      };
      if (deleted) {
        record['Deleted (UTC)'] = this.formatExportCellDate(row.deleted_at);
      }
      return record;
    });
  }

  private formatExportCellDate(value: string | null | undefined): string {
    if (value == null || value === '') {
      return '';
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    return formatDate(d, APP_DATETIME_FORMAT, 'en-GB');
  }

  toggleStatus(user: StaffUserRow): void {
    this.userApi.toggleUserStatus(user.id).subscribe({
      next: (res) => {
        if (res?.data?.status) {
          user.status = res.data.status;
        } else {
          user.status = user.status === 'Active' ? 'InActive' : 'Active';
        }
        this.snackBar.open(
          res?.message ||
          (user.status === 'Active' ? 'User activated.' : 'User deactivated.'),
          'Dismiss',
          { duration: 4000 },
        );
      },
      error: (err) => {
        this.snackBar.open(
          err?.error?.message || err?.message || 'Could not update status.',
          'Dismiss',
          { duration: 5000 },
        );
      },
    });
  }

}
